import React from "react";
import { toast } from "sonner";
import { Building2, Plus, Trash2, Edit3, RefreshCw, X, Save, Send, KeyRound } from "lucide-react";
import { adminApi } from "../lib/api";

const EMPTY = {
  company_name: "",
  cvr: "",
  contact_name: "",
  email: "",
  phone: "",
  billing_address: "",
  billing_zip: "",
  billing_city: "",
  payment_terms_days: 14,
  notes: "",
};

/**
 * SubcontractorsPanel — CRUD for vognmænd (underleverandører) der kører for EMA Kurer.
 * Each vognmand can have multiple chauffører tilknyttet via subcontractor_id.
 */
export default function SubcontractorsPanel() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(null); // { ...item } or null
  const [showForm, setShowForm] = React.useState(false);
  const [form, setForm] = React.useState(EMPTY);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get("/admin/subcontractors");
      setItems(data || []);
    } catch { toast.error("Kunne ikke hente vognmænd"); }
    finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const startEdit = (sc) => { setEditing(sc); setForm({ ...EMPTY, ...sc }); setShowForm(true); };
  const cancel = () => { setShowForm(false); setEditing(null); setForm(EMPTY); };

  const sendInvite = async (sc) => {
    if (!sc.email) { toast.error("Vognmanden har ingen email"); return; }
    if (!confirm(`Send portal-invite til ${sc.email}? De får et link til at sætte deres adgangskode.`)) return;
    try {
      const { data } = await adminApi().post(`/admin/subcontractors/${sc.id}/send-invite`);
      toast.success(`Invite sendt til ${data.sent_to}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke sende invite");
    }
  };

  const setPassword = async (sc) => {
    const pw = prompt(`Indtast ny adgangskode for ${sc.company_name} (min 8 tegn):`);
    if (!pw) return;
    if (pw.length < 8) { toast.error("Mindst 8 tegn"); return; }
    try {
      await adminApi().post(`/admin/subcontractors/${sc.id}/set-password`, { password: pw });
      toast.success(`Adgangskode sat for ${sc.company_name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke sætte adgangskode");
    }
  };

  const save = async (e) => {
    e?.preventDefault();
    if (!form.company_name.trim()) return toast.error("Firmanavn er påkrævet");
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      payload.payment_terms_days = parseInt(payload.payment_terms_days || 14, 10);
      payload.company_name = form.company_name.trim();
      if (editing) {
        await adminApi().patch(`/admin/subcontractors/${editing.id}`, payload);
        toast.success("Vognmand opdateret");
      } else {
        await adminApi().post("/admin/subcontractors", payload);
        toast.success("Vognmand oprettet");
      }
      cancel();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke gemme");
    }
  };

  const remove = async (sc) => {
    if (!window.confirm(`Slet vognmand "${sc.company_name}"?\n\nTilknyttede chauffører forbliver (men de mister deres vognmand-tilknytning).`)) return;
    try {
      await adminApi().delete(`/admin/subcontractors/${sc.id}`);
      toast.success("Vognmand slettet");
      setItems((xs) => xs.filter((x) => x.id !== sc.id));
    } catch { toast.error("Kunne ikke slette"); }
  };

  return (
    <div className="space-y-6" data-testid="subcontractors-panel">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kicker">Underleverandører · Vognmænd</div>
          <h3 className="font-display font-semibold text-2xl text-brand-dark mt-2">Vognmænd</h3>
          <p className="text-sm text-brand-text2 mt-1">Vognmænd er underleverandører hvis chauffører kører for EMA Kurer. Du kan sende månedsopgørelse til hver vognmand så de kan opkræve dig.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="btn-secondary inline-flex items-center gap-2" data-testid="sc-refresh">
            <RefreshCw size={14} /> Opdater
          </button>
          <button onClick={startNew} className="btn-primary inline-flex items-center gap-2" data-testid="sc-new">
            <Plus size={14} /> Tilføj vognmand
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={save} className="bg-brand-bg2 border-2 border-brand-red p-6" data-testid="sc-form">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-semibold text-lg text-brand-dark">
              {editing ? `Redigér ${editing.company_name}` : "Tilføj vognmand"}
            </div>
            <button type="button" onClick={cancel} className="text-brand-text2 hover:text-brand-dark" data-testid="sc-cancel">
              <X size={18} />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="label-flat">Firmanavn <span className="text-brand-red">*</span></label>
              <input className="input-flat" value={form.company_name} onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))} required data-testid="sc-company" />
            </div>
            <div>
              <label className="label-flat">CVR</label>
              <input className="input-flat" value={form.cvr || ""} onChange={(e) => setForm((f) => ({ ...f, cvr: e.target.value }))} data-testid="sc-cvr" />
            </div>
            <div>
              <label className="label-flat">Kontaktperson</label>
              <input className="input-flat" value={form.contact_name || ""} onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))} data-testid="sc-contact" />
            </div>
            <div>
              <label className="label-flat">Email</label>
              <input type="email" className="input-flat" value={form.email || ""} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="sc-email" />
            </div>
            <div>
              <label className="label-flat">Telefon</label>
              <input className="input-flat" value={form.phone || ""} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="sc-phone" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-flat">Faktureringsadresse</label>
              <input className="input-flat" value={form.billing_address || ""} onChange={(e) => setForm((f) => ({ ...f, billing_address: e.target.value }))} placeholder="Gadenavn 12" data-testid="sc-address" />
            </div>
            <div>
              <label className="label-flat">Postnr.</label>
              <input className="input-flat" value={form.billing_zip || ""} onChange={(e) => setForm((f) => ({ ...f, billing_zip: e.target.value }))} data-testid="sc-zip" />
            </div>
            <div>
              <label className="label-flat">By</label>
              <input className="input-flat" value={form.billing_city || ""} onChange={(e) => setForm((f) => ({ ...f, billing_city: e.target.value }))} data-testid="sc-city" />
            </div>
            <div>
              <label className="label-flat">Betalingsfrist (dage)</label>
              <input type="number" min={0} max={90} className="input-flat" value={form.payment_terms_days || 14} onChange={(e) => setForm((f) => ({ ...f, payment_terms_days: e.target.value }))} data-testid="sc-terms" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-flat">Noter</label>
              <textarea rows={2} className="input-flat" value={form.notes || ""} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="sc-notes" placeholder="Fx aftaler om kørselsområde, særlige rater m.v." />
            </div>
            <div className="sm:col-span-2 flex gap-2 mt-2">
              <button type="submit" className="btn-primary inline-flex items-center gap-2" data-testid="sc-save">
                <Save size={14} /> {editing ? "Gem ændringer" : "Opret vognmand"}
              </button>
              <button type="button" onClick={cancel} className="btn-secondary">Annullér</button>
            </div>
          </div>
        </form>
      )}

      {loading && items.length === 0 ? (
        <div className="text-brand-text2">Indlæser...</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-brand p-10 text-center" data-testid="sc-empty">
          <Building2 size={32} className="mx-auto text-brand-text2" />
          <div className="font-display font-semibold text-brand-dark mt-3">Ingen vognmænd endnu</div>
          <p className="text-sm text-brand-text2 mt-1">Tilføj din første vognmand for at kunne tilknytte chauffører og generere opgørelser.</p>
        </div>
      ) : (
        <div className="bg-white border border-brand overflow-x-auto" data-testid="sc-table">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-brand-text2 uppercase tracking-widest border-b border-brand bg-brand-bg2">
              <tr>
                <th className="px-4 py-3">Firma</th>
                <th className="px-4 py-3">CVR</th>
                <th className="px-4 py-3">Kontakt</th>
                <th className="px-4 py-3">Chauffører</th>
                <th className="px-4 py-3">Betaling</th>
                <th className="px-4 py-3 text-right">Handling</th>
              </tr>
            </thead>
            <tbody>
              {items.map((sc) => (
                <tr key={sc.id} className="border-t border-brand align-top">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-brand-dark flex items-center gap-2"><Building2 size={14} className="text-brand-red" />{sc.company_name}</div>
                    {sc.billing_address && (
                      <div className="text-xs text-brand-text2 mt-1">{sc.billing_address}, {sc.billing_zip} {sc.billing_city}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{sc.cvr || "—"}</td>
                  <td className="px-4 py-3">
                    <div>{sc.contact_name || "—"}</div>
                    <div className="text-xs text-brand-text2">{sc.email}</div>
                    <div className="text-xs text-brand-text2">{sc.phone}</div>
                  </td>
                  <td className="px-4 py-3 font-display font-bold">{sc.driver_count ?? 0}</td>
                  <td className="px-4 py-3 text-xs">Netto {sc.payment_terms_days} dage</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => sendInvite(sc)} className="p-1.5 border border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid={`sc-invite-${sc.id}`} title="Send portal-invite via email" disabled={!sc.email}>
                        <Send size={12} />
                      </button>
                      <button onClick={() => setPassword(sc)} className="p-1.5 border border-brand text-brand-dark hover:border-brand-dark" data-testid={`sc-setpw-${sc.id}`} title="Sæt portal-adgangskode direkte">
                        <KeyRound size={12} />
                      </button>
                      <button onClick={() => startEdit(sc)} className="p-1.5 border border-brand text-brand-dark hover:border-brand-dark" data-testid={`sc-edit-${sc.id}`} title="Redigér">
                        <Edit3 size={12} />
                      </button>
                      <button onClick={() => remove(sc)} className="p-1.5 border border-red-300 text-red-700 hover:bg-red-50" data-testid={`sc-delete-${sc.id}`} title="Slet">
                        <Trash2 size={12} />
                      </button>
                    </div>
                    {sc.portal_password_set_at && (
                      <div className="text-[10px] text-emerald-700 mt-1 uppercase tracking-wider font-bold">Portal aktiv</div>
                    )}
                    {!sc.portal_password_set_at && sc.portal_invite_sent_at && (
                      <div className="text-[10px] text-indigo-700 mt-1 uppercase tracking-wider font-bold">Invite sendt</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-slate-50 border border-slate-200 p-4 text-xs text-brand-text2 flex items-start gap-3">
        <Building2 size={16} className="flex-shrink-0 mt-0.5 text-brand-red" />
        <div>
          <strong className="text-brand-dark">Sådan bruges vognmænd:</strong> Opret en vognmand her → gå til "Chauffør-konti"-tabben og tilknyt chauffører til vognmanden. Når du derefter går til "Opgørelse"-tabben kan du eksportere en månedsopgørelse PER vognmand der viser præcis hvad de skal opkræve dig for.
        </div>
      </div>
    </div>
  );
}
